import * as THREE from 'three';

/**
 * Shenya Avatar blendshape order - VERIFIED EMPIRICALLY
 * - Indices 0-3: Custom shapes (unknown)
 * - Eyes have +4 offset (eyeBlinkLeft=4, eyeBlinkRight=5)
 * - Index 18: Extra shape between eyes and jaw
 * - Jaw/Mouth have +5 offset (jawOpen=22, mouthSmileLeft=28)
 * Source: https://unreal-shenya.readthedocs.io/en/latest/blendshapes.html
 */
const ARKIT_BLENDSHAPE_INDICES: Record<string, number> = {
  // Eyes - paired L/R (offset +4, indices 4-17)
  'eyeBlinkLeft': 4, 'eyeBlinkRight': 5,
  'eyeLookUpLeft': 6, 'eyeLookUpRight': 7,
  'eyeLookDownLeft': 8, 'eyeLookDownRight': 9,
  'eyeLookInLeft': 10, 'eyeLookInRight': 11,
  'eyeLookOutLeft': 12, 'eyeLookOutRight': 13,
  'eyeSquintLeft': 14, 'eyeSquintRight': 15,
  'eyeWideLeft': 16, 'eyeWideRight': 17,
  // Index 18: extra shape (unknown)
  // Jaw (19-22, offset +5)
  'jawForward': 19,
  'jawLeft': 20,
  'jawRight': 21,
  'jawOpen': 22,
  // Mouth (23-45, offset +5)
  'mouthClose': 23,
  'mouthFunnel': 24,
  'mouthPucker': 25,
  'mouthLeft': 26,
  'mouthRight': 27,
  'mouthSmileLeft': 28,
  'mouthSmileRight': 29,
  'mouthFrownLeft': 30,
  'mouthFrownRight': 31,
  'mouthDimpleLeft': 32,
  'mouthDimpleRight': 33,
  'mouthStretchLeft': 34,
  'mouthStretchRight': 35,
  'mouthRollLower': 36,
  'mouthRollUpper': 37,
  'mouthShrugUpper': 38,
  'mouthShrugLower': 39,
  'mouthPressLeft': 40,
  'mouthPressRight': 41,
  'mouthLowerDownLeft': 42,
  'mouthLowerDownRight': 43,
  'mouthUpperUpLeft': 44,
  'mouthUpperUpRight': 45,
  // Brow (46-50, offset +5)
  'browDownLeft': 46,
  'browDownRight': 47,
  'browInnerUp': 48,
  'browOuterUpLeft': 49,
  'browOuterUpRight': 50,
  // Cheek (51-53)
  'cheekPuff': 51,
  'cheekSquintLeft': 52,
  'cheekSquintRight': 53,
  // Nose (54-55)
  'noseSneerLeft': 54,
  'noseSneerRight': 55,
  // Tongue (56)
  'tongueOut': 56,
};

/**
 * Some UE5/Xandra exports lose their ARKit morph names and keep only
 * `body_morph_N` keys on the face mesh. Those face morphs still follow the
 * standard Azure/ARKit order, except `tongueOut` is omitted.
 */
const UE5_UNNAMED_BODY_MORPH_ORDER = [
  'eyeBlinkLeft', 'eyeLookDownLeft', 'eyeLookInLeft', 'eyeLookOutLeft', 'eyeLookUpLeft',
  'eyeSquintLeft', 'eyeWideLeft', 'eyeBlinkRight', 'eyeLookDownRight', 'eyeLookInRight',
  'eyeLookOutRight', 'eyeLookUpRight', 'eyeSquintRight', 'eyeWideRight', 'jawForward',
  'jawLeft', 'jawRight', 'jawOpen', 'mouthClose', 'mouthFunnel', 'mouthPucker',
  'mouthLeft', 'mouthRight', 'mouthSmileLeft', 'mouthSmileRight', 'mouthFrownLeft',
  'mouthFrownRight', 'mouthDimpleLeft', 'mouthDimpleRight', 'mouthStretchLeft',
  'mouthStretchRight', 'mouthRollLower', 'mouthRollUpper', 'mouthShrugLower',
  'mouthShrugUpper', 'mouthPressLeft', 'mouthPressRight', 'mouthLowerDownLeft',
  'mouthLowerDownRight', 'mouthUpperUpLeft', 'mouthUpperUpRight', 'browDownLeft',
  'browDownRight', 'browInnerUp', 'browOuterUpLeft', 'browOuterUpRight', 'cheekPuff',
  'cheekSquintLeft', 'cheekSquintRight', 'noseSneerLeft', 'noseSneerRight',
] as const;

const UE5_UNNAMED_BODY_MORPH_INDICES: Record<string, number> = Object.fromEntries(
  UE5_UNNAMED_BODY_MORPH_ORDER.map((name, index) => [name, index])
) as Record<string, number>;

function normalizeBlendshapeKey(key: string): string {
  if (key === 'tongue_out') {
    return 'tongueOut';
  }
  if (key.endsWith('_L')) {
    return `${key.slice(0, -2)}Left`;
  }
  if (key.endsWith('_R')) {
    return `${key.slice(0, -2)}Right`;
  }
  return key;
}

/**
 * Add co-articulation blending between phonemes
 * Inserts intermediate frames for smoother mouth transitions
 * Prevents "popping" between viseme shapes
 */
function addCoArticulationBlending(
  blendData: Array<{ blendshapes: Record<string, number | undefined>, time?: number }>
): Array<{ blendshapes: Record<string, number | undefined>, time?: number }> {

  if (blendData.length < 2) return blendData;

  const blendedData: Array<{ blendshapes: Record<string, number | undefined>, time?: number }> = [];
  const BLEND_DURATION = 0.03;  // 30ms blend window between phonemes

  for (let i = 0; i < blendData.length; i++) {
    const current = blendData[i];
    const next = blendData[i + 1];

    // Skip frames with null/undefined blendshapes
    if (!current || !current.blendshapes) {
      continue;
    }

    // Add current frame
    blendedData.push(current);

    // If there's a next frame with valid blendshapes, add a blend frame
    if (next && next.blendshapes && current.time !== undefined && next.time !== undefined) {
      const timeDiff = next.time - current.time;

      // Only add blend if frames are not too close (> 40ms apart)
      if (timeDiff > 0.04) {
        // Create intermediate frame at 30ms before next frame
        const blendTime = next.time - BLEND_DURATION;

        if (blendTime > current.time) {
          // Blend 70% current + 30% next (co-articulation anticipation)
          const blendedShapes: Record<string, number> = {};

          // Get all unique keys from both frames
          const allKeys = new Set([
            ...Object.keys(current.blendshapes),
            ...Object.keys(next.blendshapes)
          ]);

          allKeys.forEach(key => {
            const currentVal = current.blendshapes[key] || 0;
            const nextVal = next.blendshapes[key] || 0;
            blendedShapes[key] = currentVal * 0.7 + nextVal * 0.3;  // Anticipatory blend
          });

          blendedData.push({
            time: blendTime,
            blendshapes: blendedShapes
          });
        }
      }
    }
  }

  return blendedData;
}

/**
 * Complete mapping from Azure TTS ARKit blendshape names to avatar model morph target names.
 * Azure sends 52 ARKit blendshapes; our model supports 64 morph targets.
 *
 * Some Azure blendshapes are singular (no L/R) but need to be applied to BOTH sides.
 * These return an array of morph target names.
 */
const AZURE_TO_MODEL_MAP: Record<string, string | string[]> = {
  // Eye blink
  'eyeBlinkLeft': 'eyeBlink_L',
  'eyeBlinkRight': 'eyeBlink_R',

  // Eye look (these are already correctly named in our model)
  'eyeLookDownLeft': 'eyeLookDownLeft',
  'eyeLookDownRight': 'eyeLookDownRight',
  'eyeLookInLeft': 'eyeLookInLeft',
  'eyeLookInRight': 'eyeLookInRight',
  'eyeLookOutLeft': 'eyeLookOutLeft',
  'eyeLookOutRight': 'eyeLookOutRight',
  'eyeLookUpLeft': 'eyeLookUpLeft',
  'eyeLookUpRight': 'eyeLookUpRight',

  // Eye squint/wide
  'eyeSquintLeft': 'eyeSquint_L',
  'eyeSquintRight': 'eyeSquint_R',
  'eyeWideLeft': 'eyeWide_L',
  'eyeWideRight': 'eyeWide_R',

  // Brow
  'browDownLeft': 'browDown_L',
  'browDownRight': 'browDown_R',
  'browInnerUp': 'browInnerUp',  // Direct mapping
  'browOuterUpLeft': 'browOuterUp_L',
  'browOuterUpRight': 'browOuterUp_R',

  // Cheek
  'cheekPuff': 'cheekPuff',  // Direct mapping
  'cheekSquintLeft': 'cheekSquint_L',
  'cheekSquintRight': 'cheekSquint_R',

  // Nose
  'noseSneerLeft': 'noseSneer_L',
  'noseSneerRight': 'noseSneer_R',

  // Jaw
  'jawOpen': 'jawOpen',  // Direct mapping
  'jawForward': 'jawForward',  // Direct mapping
  'jawLeft': 'jaw_L',
  'jawRight': 'jaw_R',

  // Mouth - with L/R
  'mouthSmileLeft': 'mouthSmile_L',
  'mouthSmileRight': 'mouthSmile_R',
  'mouthFrownLeft': 'mouthFrown_L',
  'mouthFrownRight': 'mouthFrown_R',
  'mouthDimpleLeft': 'mouthDimple_L',
  'mouthDimpleRight': 'mouthDimple_R',
  'mouthStretchLeft': 'mouthStretch_L',
  'mouthStretchRight': 'mouthStretch_R',
  'mouthPressLeft': 'mouthPress_L',
  'mouthPressRight': 'mouthPress_R',
  'mouthLowerDownLeft': 'mouthLowerDown_L',
  'mouthLowerDownRight': 'mouthLowerDown_R',
  'mouthUpperUpLeft': 'mouthUpperUp_L',
  'mouthUpperUpRight': 'mouthUpperUp_R',
  'mouthLeft': 'mouth_L',
  'mouthRight': 'mouth_R',

  // Mouth - singular (apply to both sides for symmetry)
  'mouthSmile': ['mouthSmile_L', 'mouthSmile_R'],
  'mouthFrown': ['mouthFrown_L', 'mouthFrown_R'],
  'mouthDimple': ['mouthDimple_L', 'mouthDimple_R'],
  'mouthStretch': ['mouthStretch_L', 'mouthStretch_R'],
  'mouthPress': ['mouthPress_L', 'mouthPress_R'],
  'mouthLowerDown': ['mouthLowerDown_L', 'mouthLowerDown_R'],
  'mouthUpperUp': ['mouthUpperUp_L', 'mouthUpperUp_R'],

  // Mouth - direct mappings
  'mouthClose': 'mouthClose',
  'mouthFunnel': 'mouthFunnel',
  'mouthPucker': 'mouthPucker',
  'mouthRollLower': 'mouthRollLower',
  'mouthRollUpper': 'mouthRollUpper',
  'mouthShrugLower': 'mouthShrugLower',
  'mouthShrugUpper': 'mouthShrugUpper',

  // Tongue (if model supports it)
  'tongueOut': 'tongue_out',
};

/**
 * Get the morph target name(s) for an Azure blendshape.
 * Returns an array of morph target names (usually 1, sometimes 2 for bilateral).
 *
 * For maximum compatibility, returns:
 * 1. The mapped name (e.g., eyeBlink_L for eyeBlinkLeft)
 * 2. The original Azure/ARKit name (e.g., eyeBlinkLeft)
 * 3. Legacy L/R transformations
 * 4. ARKit numeric index as string (e.g., "17" for jawOpen) - for models with numeric keys
 */
export function getMorphTargets(azureKey: string): string[] {
  const normalizedKey = normalizeBlendshapeKey(azureKey);
  const mapped = AZURE_TO_MODEL_MAP[normalizedKey];
  const results: string[] = [];

  if (mapped) {
    if (Array.isArray(mapped)) {
      results.push(...mapped);
    } else {
      results.push(mapped);
    }
  }

  // Always try the original Azure key as a fallback (some models use ARKit names directly)
  if (!results.includes(normalizedKey)) {
    results.push(normalizedKey);
  }

  if (normalizedKey !== azureKey && !results.includes(azureKey)) {
    results.push(azureKey);
  }

  // Also try legacy transformation for L/R keys
  if (normalizedKey.endsWith('Right')) {
    const transformed = normalizedKey.replace('Right', '_R');
    if (!results.includes(transformed)) {
      results.push(transformed);
    }
  }
  if (normalizedKey.endsWith('Left')) {
    const transformed = normalizedKey.replace('Left', '_L');
    if (!results.includes(transformed)) {
      results.push(transformed);
    }
  }

  // For models with numeric morph target keys (like Shenya), try the ARKit index as a string
  // This is a fallback for models exported without morph target names
  if (normalizedKey in ARKIT_BLENDSHAPE_INDICES) {
    const arkitIndex = ARKIT_BLENDSHAPE_INDICES[normalizedKey].toString();
    if (!results.includes(arkitIndex)) {
      results.push(arkitIndex);
    }
  }

  // Some UE5/Xandra exports keep unnamed `body_morph_N` targets instead of
  // facial morph names. Add those candidates so visemes still resolve.
  if (normalizedKey in UE5_UNNAMED_BODY_MORPH_INDICES) {
    const unnamedBodyMorph = `body_morph_${UE5_UNNAMED_BODY_MORPH_INDICES[normalizedKey]}`;
    if (!results.includes(unnamedBodyMorph)) {
      results.push(unnamedBodyMorph);
    }
  }

  // A few older exports retain `target_N` rather than `body_morph_N`.
  if (normalizedKey in UE5_UNNAMED_BODY_MORPH_INDICES) {
    const targetName = `target_${UE5_UNNAMED_BODY_MORPH_INDICES[normalizedKey]}`;
    if (!results.includes(targetName)) {
      results.push(targetName);
    }
  }

  return results;
}

export function resolveMorphTargetIndex(
  morphTargetDictionary: Record<string, number> | null | undefined,
  azureKey: string
): number | undefined {
  if (!morphTargetDictionary) {
    return undefined;
  }

  const candidates = getMorphTargets(azureKey);
  for (const candidate of candidates) {
    const index = morphTargetDictionary[candidate];
    if (index !== undefined) {
      return index;
    }
  }

  return undefined;
}

function modifiedKey(key: string): string {
  // For backward compatibility, return the first morph target
  const targets = getMorphTargets(key);
  return targets[0];
}

// Track which blendshapes we've already warned about (to avoid spam)
const warnedMissingBlendshapes = new Set<string>();
const loggedBodyParts = new Set<string>();

/**
 * SAFE MOUTH BLENDSHAPES - Only the essential ones for lip-sync.
 * These are the safest, most tested blendshapes that won't crash Three.js.
 * Avoids: eyes, brows, cheeks, nose, tongue, body - only pure mouth/jaw.
 */
const SAFE_MOUTH_BLENDSHAPES = new Set([
  // Jaw - essential for speech
  'jawOpen',
  'jawForward',

  // Mouth shapes - essential for visemes
  'mouthClose',
  'mouthFunnel',
  'mouthPucker',
  'mouthSmileLeft', 'mouthSmileRight', 'mouthSmile',
  'mouthFrownLeft', 'mouthFrownRight', 'mouthFrown',
  'mouthDimpleLeft', 'mouthDimpleRight', 'mouthDimple',
  'mouthStretchLeft', 'mouthStretchRight', 'mouthStretch',
  'mouthRollLower', 'mouthRollUpper',
  'mouthShrugLower', 'mouthShrugUpper',
  'mouthPressLeft', 'mouthPressRight', 'mouthPress',
  'mouthLowerDownLeft', 'mouthLowerDownRight', 'mouthLowerDown',
  'mouthUpperUpLeft', 'mouthUpperUpRight', 'mouthUpperUp',
  'mouthLeft', 'mouthRight',

  // Expressiveness - brows and cheeks for natural speaking
  'browInnerUp',
  'browDownLeft', 'browDownRight',
  'browOuterUpLeft', 'browOuterUpRight',
  'cheekSquintLeft', 'cheekSquintRight',
]);

/**
 * Blendshape value scaling factors to improve animation quality.
 * These values are aligned with PHONEME_SCALING in speechUtils.ts which
 * works correctly for standalone avatar. Visual IVR should use the same values.
 *
 * IMPORTANT: Keep in sync with speechUtils.ts PHONEME_SCALING
 *
 * v1.9.10 (Jan 28, 2026): Reduced jaw and mouth values to fix teeth exposure
 * on second+ animations. Previous values showed too much teeth after
 * the animation system "warmed up".
 */
const BLENDSHAPE_SCALE_FACTORS: Record<string, number> = {
  // Jaw movements - reduced to prevent excessive mouth opening and teeth exposure
  'jawOpen': 0.70,           // Reduced from 0.80 to minimize teeth exposure
  'jawForward': 0.90,
  'jawLeft': 1.05,
  'jawRight': 1.05,

  // Lower lip - reduced to prevent bottom teeth exposure
  'mouthLowerDownLeft': 0.45,  // Reduced from 0.55
  'mouthLowerDownRight': 0.45,
  'mouthLowerDown': 0.45,

  // Upper lip - reduced to prevent upper teeth exposure
  'mouthUpperUpLeft': 0.95,    // Reduced from 1.25
  'mouthUpperUpRight': 0.95,
  'mouthUpperUp': 0.95,

  // Mouth shapes - moderate enhancement for clarity
  'mouthFunnel': 1.25,
  'mouthPucker': 1.25,
  'mouthClose': 0.65,

  // Smile and expression - subtle enhancement
  'mouthSmileLeft': 1.1,
  'mouthSmileRight': 1.1,
  'mouthSmile': 1.1,
  'mouthFrownLeft': 1.05,
  'mouthFrownRight': 1.05,
  'mouthFrown': 1.05,

  // Stretch - slight enhancement
  'mouthStretchLeft': 1.1,
  'mouthStretchRight': 1.1,
  'mouthStretch': 1.1,

  // Lip roll - reduce for natural speech
  'mouthRollLower': 0.75,
  'mouthRollUpper': 0.75,

  // Cheek - slight enhancement
  'cheekPuff': 1.1,
  'cheekSquintLeft': 1.1,
  'cheekSquintRight': 1.1,

  // Dimples - subtle
  'mouthDimpleLeft': 1.05,
  'mouthDimpleRight': 1.05,
  'mouthDimple': 1.05,

  // Enhance expressiveness (brows)
  'browInnerUp': 1.2,
  'browOuterUpLeft': 1.1,
  'browOuterUpRight': 1.1,
};

export default function createAnimation(
  blendData: Array<{ blendshapes: Record<string, number | undefined>, time?: number }>,
  morphTargetDictionary: Record<string, number>,
  bodyPart: string,
  fps: number = 30,
  duration: number = 0,
  interpolation: THREE.InterpolationModes = THREE.InterpolateLinear,  // Keep as parameter but we'll use custom smoothing
  enableCoArticulation: boolean = true,  // Co-articulation smoothing - adds transition frames for smoother mouth movement
  safeMode: boolean = true  // SAFE MODE: Only use essential mouth blendshapes to prevent crashes
): THREE.AnimationClip | null {

  // Guard: Return null if morphTargetDictionary is null/undefined
  if (!morphTargetDictionary) {
    console.warn('⚠️ [Converter] morphTargetDictionary is null/undefined for', bodyPart);
    return null;
  }

  // Guard: Return null if blendData is empty or invalid
  if (!blendData || blendData.length === 0) {
    console.warn('⚠️ [Converter] blendData is empty for', bodyPart);
    return null;
  }

  const shouldLogOnce = morphTargetDictionary && !loggedBodyParts.has(bodyPart);
  if (shouldLogOnce) {
    loggedBodyParts.add(bodyPart);
    console.debug(`🎯 [Converter] ${bodyPart}: ${Object.keys(morphTargetDictionary).length} morph targets`);
  }

  if (blendData.length !== 0) {
    // CO-ARTICULATION: Add blend frames between phonemes for smoother transitions
    const processedBlendData = enableCoArticulation ? addCoArticulationBlending(blendData) : blendData;

    // Track used vs ignored blendshapes for this animation
    const usedBlendshapes = new Set<string>();
    const ignoredBlendshapes = new Set<string>();

    // FIRST PASS: Identify all morph target indices that will be used
    // This is critical to ensure we allocate values for every frame
    const usedMorphTargetIndices = new Set<number>();
    const morphTargetKeyMapping = new Map<number, string>(); // idx -> original key for debugging

    const notFoundMorphTargets = new Set<string>(); // Track morph targets we couldn't find
    const filteredOutKeys = new Set<string>(); // Track keys filtered by safeMode
    const foundMorphTargets = new Set<string>(); // Track successful matches

    processedBlendData.forEach((frame) => {
      if (!frame || !frame.blendshapes) return;
      Object.keys(frame.blendshapes).forEach((key) => {
        if (safeMode && !SAFE_MOUTH_BLENDSHAPES.has(key)) {
          filteredOutKeys.add(key);
          return;
        }
        const morphTargets = getMorphTargets(key);
        let foundAny = false;
        morphTargets.forEach(morphTarget => {
          if (morphTarget in morphTargetDictionary) {
            foundAny = true;
            foundMorphTargets.add(morphTarget);
            const idx = morphTargetDictionary[morphTarget];
            usedMorphTargetIndices.add(idx);
            if (!morphTargetKeyMapping.has(idx)) {
              morphTargetKeyMapping.set(idx, key);
            }
          } else {
            notFoundMorphTargets.add(morphTarget);
          }
        });
      });
    });

    if (usedMorphTargetIndices.size === 0 && notFoundMorphTargets.size > 0) {
      console.warn('❌ [Converter] No morph targets matched! Tried:', Array.from(notFoundMorphTargets).slice(0, 15));
    }

    // Initialize animation arrays ONLY for used morph targets
    // Map from morphTarget index to its animation values
    const animationByIdx = new Map<number, number[]>();
    usedMorphTargetIndices.forEach(idx => {
      animationByIdx.set(idx, []);
    });

    let time: number[] = [];
    let finishedFrames = 0;

    // SECOND PASS: Build animation data with proper alignment
    // Every used morph target gets a value for every frame
    processedBlendData.forEach((d, i) => {
      // Collect values for this frame into a temporary map
      const frameValues = new Map<number, number>();

      if (d && d.blendshapes) {
        Object.entries(d.blendshapes).forEach(([key, value]: [string, number | undefined]) => {
          if (safeMode && !SAFE_MOUTH_BLENDSHAPES.has(key)) return;

          const morphTargets = getMorphTargets(key);
          let foundAny = false;

          for (const morphTarget of morphTargets) {
            if (!(morphTarget in morphTargetDictionary)) continue;

            foundAny = true;
            const idx = morphTargetDictionary[morphTarget];

            if (value !== undefined) {
              let adjustedValue = value;
              // Apply scaling factors for better animation quality
              if (key in BLENDSHAPE_SCALE_FACTORS) {
                adjustedValue *= BLENDSHAPE_SCALE_FACTORS[key];
              }
              if (key === 'mouthShrugUpper') {
                adjustedValue += 0.4;
              }
              // Clamp to valid range [0, 1]
              adjustedValue = Math.max(0, Math.min(1, adjustedValue));
              frameValues.set(idx, Number(adjustedValue));
            }
          }

          if (foundAny) {
            usedBlendshapes.add(key);
          } else if (!warnedMissingBlendshapes.has(key)) {
            ignoredBlendshapes.add(key);
          }
        });
      }

      // Push values for ALL used morph targets (use previous value or 0 if missing)
      usedMorphTargetIndices.forEach(idx => {
        const arr = animationByIdx.get(idx)!;
        if (frameValues.has(idx)) {
          arr.push(frameValues.get(idx)!);
        } else {
          // Use previous value for continuity, or 0 if first frame
          const prevValue = arr.length > 0 ? arr[arr.length - 1] : 0;
          arr.push(prevValue);
        }
      });

      // Record time for this frame
      if (d?.time !== undefined) {
        time.push(d.time);
      } else {
        time.push(finishedFrames / fps);
      }
      finishedFrames++;
    });

    // Convert to old format for track creation (indexed by morph target index)
    let animation: number[][] = [];
    for (let i = 0; i < Object.keys(morphTargetDictionary).length; i++) {
      animation.push(animationByIdx.get(i) || []);
    }

    // Track ignored blendshapes to avoid repeated warnings
    if (ignoredBlendshapes.size > 0) {
      const newIgnored = Array.from(ignoredBlendshapes);
      newIgnored.forEach(key => warnedMissingBlendshapes.add(key));
    }

    let tracks: THREE.KeyframeTrack[] = [];

    // Create morph animation tracks
    // Use a Set to track which morph targets we've already added tracks for
    const addedMorphTargets = new Set<number>();

    // Collect ALL unique blendshape keys from ALL frames (not just first frame)
    // This ensures we create tracks for shapes that appear later in the animation
    const allBlendshapeKeys = new Set<string>();
    processedBlendData.forEach(frame => {
      if (frame && frame.blendshapes) {
        Object.keys(frame.blendshapes).forEach(key => allBlendshapeKeys.add(key));
      }
    });

    if (allBlendshapeKeys.size === 0) {
      console.warn('⚠️ [Converter] No valid blendshape frames found!');
      return null;
    }

    // Create tracks for all blendshapes that exist in the dictionary
    allBlendshapeKeys.forEach(key => {
      // SAFE MODE: Skip non-mouth blendshapes to prevent crashes
      if (safeMode && !SAFE_MOUTH_BLENDSHAPES.has(key)) {
        return;
      }

      const morphTargets = getMorphTargets(key);

      morphTargets.forEach(morphTarget => {
        if (!(morphTarget in morphTargetDictionary)) {
          return;
        }

        const i = morphTargetDictionary[morphTarget];

        // Avoid duplicate tracks for the same morph target
        if (addedMorphTargets.has(i)) {
          return;
        }
        addedMorphTargets.add(i);

        // Skip if we don't have animation data for this morph target
        if (!animation[i] || animation[i].length === 0) {
          return;
        }

        const track = new THREE.NumberKeyframeTrack(
          `${bodyPart}.morphTargetInfluences[${i}]`,
          time,
          animation[i],
          THREE.InterpolateLinear  // Linear = accurate, no smoothing artifacts
        );

        tracks.push(track);
      });
    });

    const clip = new THREE.AnimationClip('animation', -1, tracks);

    // Only warn if no tracks created (likely an issue)
    if (tracks.length === 0) {
      console.warn(`⚠️ [Converter] NO tracks created for ${bodyPart}! Animation will be empty.`);
    }

    return clip
  }
  return null
}
